"use client";

import { useState, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { MapPin } from "lucide-react";
import Image from "next/image";

// Brand data - replace with real brands when ready
// Add `logo` field with image path to show actual logo instead of initials
interface Brand {
  name: string;
  location: string;
  flag: string;
  logo?: string; // Optional - path to logo image
}

// Row 1 brands (scroll left)
const brandsRow1: Brand[] = [
  { name: "StyleVault", location: "New York, USA", flag: "🇺🇸" },
  { name: "NordicWear", location: "Stockholm, Sweden", flag: "🇸🇪" },
  { name: "Tokyo Trends", location: "Tokyo, Japan", flag: "🇯🇵" },
  { name: "BerlinGoods", location: "Berlin, Germany", flag: "🇩🇪" },
  { name: "AussieOutdoors", location: "Sydney, Australia", flag: "🇦🇺" },
  { name: "ParisChic", location: "Paris, France", flag: "🇫🇷" },
  { name: "LondonLux", location: "London, UK", flag: "🇬🇧" },
  { name: "TorontoTech", location: "Toronto, Canada", flag: "🇨🇦" },
  { name: "DubaiStyle", location: "Dubai, UAE", flag: "🇦🇪" },
  { name: "AmsterdamCo", location: "Amsterdam, Netherlands", flag: "🇳🇱" },
  { name: "MilanModa", location: "Milan, Italy", flag: "🇮🇹" },
  { name: "SeoulSelect", location: "Seoul, South Korea", flag: "🇰🇷" },
  { name: "SaoPauloShop", location: "São Paulo, Brazil", flag: "🇧🇷" },
  { name: "MexicoMart", location: "Mexico City, Mexico", flag: "🇲🇽" },
];

// Row 2 brands (scroll right)
const brandsRow2: Brand[] = [
  { name: "SingaporeStore", location: "Singapore", flag: "🇸🇬" },
  { name: "MumbaiMarket", location: "Mumbai, India", flag: "🇮🇳" },
  { name: "CapeTownCo", location: "Cape Town, South Africa", flag: "🇿🇦" },
  { name: "ViennaVogue", location: "Vienna, Austria", flag: "🇦🇹" },
  { name: "ZurichZone", location: "Zurich, Switzerland", flag: "🇨🇭" },
  { name: "BuenosAires", location: "Buenos Aires, Argentina", flag: "🇦🇷" },
  { name: "BangkokBrand", location: "Bangkok, Thailand", flag: "🇹🇭" },
  { name: "LisbonLooks", location: "Lisbon, Portugal", flag: "🇵🇹" },
  { name: "AthensApparel", location: "Athens, Greece", flag: "🇬🇷" },
  { name: "PragueStyle", location: "Prague, Czech Republic", flag: "🇨🇿" },
  { name: "WarsawWear", location: "Warsaw, Poland", flag: "🇵🇱" },
  { name: "OsloOutfit", location: "Oslo, Norway", flag: "🇳🇴" },
  { name: "HelsinkiHub", location: "Helsinki, Finland", flag: "🇫🇮" },
  { name: "DublinDrip", location: "Dublin, Ireland", flag: "🇮🇪" },
];

function BrandCard({ name, location, flag, logo }: Brand) {
  // Generate a pseudo-random color based on brand name for the logo placeholder
  const colors = [
    "bg-primary-100 text-primary-700",
    "bg-secondary-100 text-secondary-700",
    "bg-blue-100 text-blue-700",
    "bg-amber-100 text-amber-700",
    "bg-rose-100 text-rose-700",
    "bg-emerald-100 text-emerald-700",
  ];
  const colorIndex = name.length % colors.length;
  const colorClass = colors[colorIndex];

  // Get initials from name
  const initials = name
    .split(/(?=[A-Z])/)
    .map((word) => word[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="flex-shrink-0 w-[180px] bg-white rounded-xl border border-stone-100 p-3 shadow-sm hover:shadow-md transition-all hover:scale-[1.02]">
      <div className="flex items-center gap-3">
        {/* Logo or placeholder */}
        {logo ? (
          <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0">
            <Image
              src={logo}
              alt={`${name} logo`}
              width={40}
              height={40}
              className="w-full h-full object-cover"
            />
          </div>
        ) : (
          <div
            className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm flex-shrink-0 ${colorClass}`}
          >
            {initials}
          </div>
        )}
        {/* Brand info */}
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-stone-900 text-sm truncate">
            {name}
          </p>
          <div className="flex items-center gap-1 text-xs text-stone-500">
            <span>{flag}</span>
            <MapPin className="w-3 h-3" />
            <span className="truncate">{location.split(",")[0]}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function CarouselRow({
  brands,
  direction,
  delay = 0,
  isPaused,
}: {
  brands: Brand[];
  direction: "left" | "right";
  delay?: number;
  isPaused: boolean;
}) {
  // Double the brands for seamless infinite scroll
  const duplicatedBrands = [...brands, ...brands];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
      className="relative overflow-hidden py-2"
    >
      <div
        className={`flex gap-3 ${
          direction === "left"
            ? "animate-scroll-left-fast"
            : "animate-scroll-right-fast"
        }`}
        style={{
          animationPlayState: isPaused ? "paused" : "running",
        }}
      >
        {duplicatedBrands.map((brand, index) => (
          <BrandCard
            key={`${brand.name}-${index}`}
            name={brand.name}
            location={brand.location}
            flag={brand.flag}
            logo={brand.logo}
          />
        ))}
      </div>
    </motion.div>
  );
}

export function TrustBar() {
  const [isPaused, setIsPaused] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  // Mouse handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!containerRef.current) return;
    setIsDragging(true);
    setIsPaused(true);
    startX.current = e.pageX - containerRef.current.offsetLeft;
    scrollLeft.current = containerRef.current.scrollLeft;
    e.preventDefault();
  }, []);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging || !containerRef.current) return;
      const x = e.pageX - containerRef.current.offsetLeft;
      const walk = (x - startX.current) * 1.5;
      containerRef.current.scrollLeft = scrollLeft.current - walk;
    },
    [isDragging]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setTimeout(() => setIsPaused(false), 150);
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      setTimeout(() => setIsPaused(false), 150);
    }
  }, [isDragging]);

  // Touch handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (!containerRef.current) return;
    setIsDragging(true);
    setIsPaused(true);
    startX.current = e.touches[0].pageX - containerRef.current.offsetLeft;
    scrollLeft.current = containerRef.current.scrollLeft;
  }, []);

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isDragging || !containerRef.current) return;
      const x = e.touches[0].pageX - containerRef.current.offsetLeft;
      const walk = (x - startX.current) * 1.5;
      containerRef.current.scrollLeft = scrollLeft.current - walk;
    },
    [isDragging]
  );

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
    setTimeout(() => setIsPaused(false), 150);
  }, []);

  return (
    <section className="py-8 bg-stone-50/80 border-y border-stone-100 overflow-hidden">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="text-center mb-6"
      >
        <p className="text-lg font-semibold text-stone-500">
          Trusted by brands in 25+ countries
        </p>
      </motion.div>

      {/* Two-row carousel with hover pause */}
      <div className="relative">
        {/* Gradient fade on edges */}
        <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-stone-50 to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-stone-50 to-transparent z-10 pointer-events-none" />

        {/* Draggable carousel container */}
        <div
          ref={containerRef}
          className={`draggable-carousel ${isDragging ? "is-dragging" : ""}`}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Carousel rows */}
          <div className="flex flex-col gap-2">
            <CarouselRow
              brands={brandsRow1}
              direction="left"
              delay={0.1}
              isPaused={isPaused}
            />
            <CarouselRow
              brands={brandsRow2}
              direction="right"
              delay={0.2}
              isPaused={isPaused}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
